import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaImage,
  FaTimes,
  FaUpload,
  FaArrowUp,
  FaArrowDown,
  FaCheckCircle,
  FaHeading,
  FaEdit
} from 'react-icons/fa';

import { database, storage } from '../firebase';
import { ref as dbRef, onValue, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const AdminHomepage = () => {
  const [sliders, setSliders] = useState([]);
  const [aboutUs, setAboutUs] = useState({
    header: '',
    description: '',
    imageUrl: '',
    preview: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const sliderFileRefs = useRef({});
  const aboutUsFileRef = useRef(null);
  const newSliderRef = useRef(null);

  // Fetch homepage data from Firebase
  useEffect(() => {
    const homepageRef = dbRef(database, 'Dr/homepage');
    const unsubscribe = onValue(homepageRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Map sliders and add preview property for displaying images
        const slidersWithPreview = (data.sliders || []).map(slider => ({
          ...slider,
          preview: slider.imageUrl || '' // Set preview to imageUrl for display
        }));
        setSliders(slidersWithPreview);
        setAboutUs({
          header: data.aboutUs?.header || '',
          description: data.aboutUs?.description || '',
          imageUrl: data.aboutUs?.imageUrl || '',
          preview: data.aboutUs?.imageUrl || ''
        });
      } else {
        // Initialize with empty structure
        setSliders([]);
        setAboutUs({ header: '', description: '', imageUrl: '', preview: '' });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle Slider Image Upload
  const handleSliderImageUpload = async (id, file) => {
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setSliders(prev => prev.map(s => 
        s.id === id ? { ...s, preview: reader.result, uploading: true } : s
      ));
    };
    reader.readAsDataURL(file);

    // Upload to Firebase Storage
    try {
      const imageRef = storageRef(storage, `Dr/homepage/sliders/${id}_${Date.now()}.jpg`);
      await uploadBytes(imageRef, file);
      const downloadUrl = await getDownloadURL(imageRef);

      setSliders(prev => prev.map(s => 
        s.id === id ? { ...s, imageUrl: downloadUrl, preview: downloadUrl, uploading: false } : s
      ));
    } catch (error) {
      console.error('Upload failed:', error);
      setSliders(prev => prev.map(s => 
        s.id === id ? { ...s, uploading: false } : s
      ));
    }
  };

  // Handle About Us Image Upload
  const handleAboutUsImageUpload = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setAboutUs(prev => ({ ...prev, preview: reader.result, uploading: true }));
    };
    reader.readAsDataURL(file);

    try {
      const imageRef = storageRef(storage, `Dr/homepage/about-us_${Date.now()}.jpg`);
      await uploadBytes(imageRef, file);
      const downloadUrl = await getDownloadURL(imageRef);

      setAboutUs(prev => ({
        ...prev,
        imageUrl: downloadUrl,
        preview: downloadUrl,
        uploading: false
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      setAboutUs(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleAboutUsTextChange = (field, value) => {
    setAboutUs(prev => ({ ...prev, [field]: value }));
  };

  // Add New Slider
  const addNewSlider = () => {
    const newId = Date.now();
    const newSlider = {
      id: newId,
      imageUrl: '',
      preview: ''
    };
    setSliders(prev => [...prev, newSlider]);

    setTimeout(() => {
      newSliderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Remove Slider
  const removeSlider = (id) => {
    setSliders(prev => prev.filter(s => s.id !== id));
  };

  // Reorder Sliders
  const moveSlider = (index, direction) => {
    const newSliders = [...sliders];
    if (direction === 'up' && index > 0) {
      [newSliders[index], newSliders[index - 1]] = [newSliders[index - 1], newSliders[index]];
    } else if (direction === 'down' && index < newSliders.length - 1) {
      [newSliders[index], newSliders[index + 1]] = [newSliders[index + 1], newSliders[index]];
    }
    setSliders(newSliders);
  };

  // Auto-expand textarea
  const autoExpand = (e) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  // Save All to Firebase
  const saveAllChanges = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        sliders: sliders.map(s => ({
          id: s.id,
          imageUrl: s.imageUrl || ''
        })),
        aboutUs: {
          header: aboutUs.header,
          description: aboutUs.description,
          imageUrl: aboutUs.imageUrl || ''
        }
      };

      await set(dbRef(database, 'Dr/homepage'), dataToSave);
      setSuccess('Homepage content saved successfully!');
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save. Check console.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading homepage content...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* <nav className="text-sm text-gray-600 mb-4">
            <Link to="/admin/dashboard" className="hover:text-blue-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <span className="font-medium">Homepage Management</span>
          </nav> */}
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Homepage Content</h1>
              <p className="text-gray-600 mt-2">Manage sliders and About Us section</p>
            </div>
            <button
              onClick={saveAllChanges}
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg flex items-center gap-3 font-semibold"
            >
              {saving ? 'Saving...' : <><FaSave /> Save All Changes</>}
            </button>
          </div>
        </div>

        {success && (
          <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-2xl flex items-center">
            <FaCheckCircle className="text-green-600 text-2xl mr-3" />
            <p className="text-green-800 font-semibold">{success}</p>
          </div>
        )}

        {/* Sliders Section */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaImage className="mr-3 text-blue-600" />
              Hero Sliders ({sliders.length})
            </h2>
            <button
              onClick={addNewSlider}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl flex items-center gap-2"
            >
              <FaPlus /> Add Slider
            </button>
          </div>

          <div className="space-y-8">
            {sliders.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                <FaImage className="text-6xl text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No sliders added yet</p>
                <p className="text-gray-500">Click "Add Slider" to create your first one</p>
              </div>
            ) : (
              sliders.map((slider, index) => (
                <div
                  key={slider.id}
                  ref={index === sliders.length - 1 ? newSliderRef : null}
                  className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold">Slider {index + 1}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => moveSlider(index, 'up')}
                        disabled={index === 0}
                        className={`p-3 rounded-lg ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        onClick={() => moveSlider(index, 'down')}
                        disabled={index === sliders.length - 1}
                        className={`p-3 rounded-lg ${index === sliders.length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        onClick={() => removeSlider(slider.id)}
                        className="p-3 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-8">
                    {/* Image Only Upload */}
                    <div>
                      <label className="block font-medium text-gray-700 mb-3 text-lg">Slider Image</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center min-h-[400px] flex flex-col items-center justify-center relative bg-gray-50">
                        {slider.preview ? (
                          <div className="relative w-full h-full group">
                            <img src={slider.preview} alt="Preview" className="w-full h-auto max-h-[500px] mx-auto object-contain rounded-lg shadow-md" />
                            {slider.uploading && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg text-white">
                                <span className="animate-pulse">Uploading...</span>
                              </div>
                            )}
                            {/* Edit Button Overlay */}
                            <button
                              onClick={() => sliderFileRefs.current[slider.id]?.click()}
                              className="absolute top-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-xl shadow-xl transition-all flex items-center gap-2 font-bold"
                              title="Edit Image"
                            >
                              <FaEdit className="text-xl" />
                              <span>Change Image</span>
                            </button>
                          </div>
                        ) : (
                          <div 
                            onClick={() => sliderFileRefs.current[slider.id]?.click()}
                            className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white rounded-lg transition-all p-12"
                          >
                            <FaUpload className="text-6xl text-blue-400 mb-4" />
                            <p className="text-xl font-semibold text-gray-700">Click to upload banner</p>
                            <p className="text-gray-500 mt-2">Recommended: High-resolution landscape image</p>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          ref={el => sliderFileRefs.current[slider.id] = el}
                          onChange={(e) => handleSliderImageUpload(slider.id, e.target.files[0])}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* About Us Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
            <FaHeading className="mr-3 text-purple-600" />
            About Us Section
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Text */}
            <div className="space-y-6">
              <div>
                <label className="block font-medium text-gray-700 mb-2">Section Header</label>
                <input
                  type="text"
                  value={aboutUs.header}
                  onChange={(e) => handleAboutUsTextChange('header', e.target.value)}
                  placeholder="e.g., Our Journey of Excellence"
                  className="w-full p-4 border rounded-xl focus:ring-2 focus:ring-purple-500 text-xl font-bold"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={aboutUs.description}
                  onChange={(e) => handleAboutUsTextChange('description', e.target.value)}
                  onInput={autoExpand}
                  rows="10"
                  placeholder="Write about your hospital's journey, mission, and values..."
                  className="w-full p-5 border rounded-xl focus:ring-2 focus:ring-purple-500 resize-none overflow-hidden text-lg leading-relaxed"
                />
              </div>
              <div className="p-5 bg-purple-50 rounded-xl border border-purple-200">
                <h4 className="font-bold text-xl mb-3">{aboutUs.header || 'Section Header'}</h4>
                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {aboutUs.description || 'Your detailed description will appear here...'}
                </div>
              </div>
            </div>

            {/* Image */}
            <div>
              <label className="block font-medium text-gray-700 mb-3">About Us Image</label>
              <div
                onClick={() => aboutUsFileRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center cursor-pointer hover:border-purple-400 transition-all h-96 flex flex-col items-center justify-center"
              >
                {aboutUs.preview ? (
                  <div className="relative w-full h-full">
                    <img src={aboutUs.preview} alt="About Us" className="w-full h-full object-cover rounded-xl" />
                    {aboutUs.uploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                        <div className="text-white text-lg">Uploading...</div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                      <span className="text-white font-medium text-xl">Change Image</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <FaUpload className="text-6xl text-gray-400 mb-4" />
                    <p className="text-xl text-gray-600">Click to upload image</p>
                    <p className="text-gray-500">Recommended: 800×600px</p>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={aboutUsFileRef}
                  onChange={(e) => handleAboutUsImageUpload(e.target.files[0])}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Final Save */}
        <div className="mt-12 flex justify-end">
          <button
            onClick={saveAllChanges}
            disabled={saving}
            className="px-12 py-5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xl font-bold rounded-2xl hover:shadow-2xl flex items-center gap-4"
          >
            {saving ? (
              <>Saving Changes...</>
            ) : (
              <>
                <FaSave className="text-2xl" />
                Save All Changes to Homepage
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHomepage;